import { Box, Divider, Grid, Typography } from "@mui/material";
import theme from "Theme/AppTheme";
import PatientJourneyStagesDialog from "components/UI/PatientJourneyStagesDialog";
import { observer } from "mobx-react";
import { useMemo } from "react";
import { useStore } from "utils/useStore";
import { MINIMUM_CHART_PLOT_HEIGHT_PX } from "../../Dashboard/Layout/layoutConfig";
import { SignalsEndpoints } from "../../SignalsModels";
// import SignalsSpotlight from "./SignalsSpotlight";
import ChartCardStore from "./ChartCardStore";
import { useChartCardLayout } from "./useChartCardLayout";
import { useCurrentDashboard } from "components/Signals/useCurrentDashboard";
import {
    AcxDeprecatedTag,
    useStatusTagVisible,
} from "components/UI/StatusTags";
import { AuthStore } from "stores/AuthStore";
import Chart from "./Chart";
import Spotlight from "./Spotlight";
import { SignalsAnalyzeWithKboSpotlight } from "./AnalyzeWithKBoSpotlight";
import ChartControls from "./ChartControls";
import ChartPagination from "./ChartControls/ChartPagination";
import ExportSelectionFields from "./ChartControls/ExportSelectionFields";
import { ChartMenu } from "./ChartMenu/ChartMenu";
import AcxDataGrid from "components/UI/AcxDataGrid/AcxDataGrid";
import { defaultShadowsForSignals } from "components/Signals/SignalsUtils";
import AcxEmptyState from "components/UI/AcxEmptyStateIndicator/AcxEmptyState";
import { ObjectFiltersChips } from "../ObjectFilters";
import ZoomBreadcrumbs from "./ZoomBreadcrumbs";
import DataAvailabilityInfo from "components/Signals/DataAvailability/DataAvailabilityInfo";
import { resolveDataAvailability } from "components/Signals/DataAvailability/DataAvailability";
import SignalsPageStore from "components/Signals/Store/SignalsPageStore";
import {
    buildDataGridVisibilityKey,
    useDataGridVisibility,
} from "./dataGridVisibility";
import { DataGridVisibilityToggle } from "./DataGridVisibilityToggle";

const ChartCard = observer(({ store }: { store: ChartCardStore }) => {
    const authStore = useStore(AuthStore);
    const signalsPageStore = useStore(SignalsPageStore);
    const currentDashboard = useCurrentDashboard();

    // Presentation mode is a clean read-only view, so the deprecation marker is suppressed there.
    // `.no-export` additionally strips it from the per-chart PNG export.
    // Called unconditionally: the tag may declare a permission, and hooks cannot be
    // called behind the Deprecated/presentation-mode checks below.
    const isStatusTagVisible = useStatusTagVisible(store.config.status);
    const deprecation =
        isStatusTagVisible &&
        store.config.status?.tag === "Deprecated" &&
        !currentDashboard?.isPresentationMode
            ? store.config.status
            : undefined;

    const isValidData = store.hasRenderableChartData;
    const dataAvailability = resolveDataAvailability({
        authStore,
        policy: store.config.dataAvailability,
        defaultEmptyStateVariant: store.config.renderOptions.emptyStateVariant,
        startDate: store.state?.startDate,
        endDate: store.state?.endDate,
        hasRenderableData: isValidData,
    });
    const showDataAvailabilityInfo =
        !!dataAvailability.infoKind &&
        store.isDisplayingRenderableChartData &&
        !store.isPresentationMode &&
        !currentDashboard?.isExportCaptureInProgress;

    const {
        chartContainerRef,
        attachCard,
        chartBodyRef,
        chartSizeRef,
        dataGridFrameRef,
    } = useChartCardLayout(store);

    const shouldShowSpotlight =
        store.showSpotlight && authStore.canUserView("AI Summaries");
    const shouldShowAnalyzeWithKBo =
        store.showAnalyzeWithKBo && authStore.canUserView("AI Summaries");
    const renderedRowCount = store.dataGridStore?.rows?.length ?? 0;

    const dataGridHeight = useMemo(() => {
        const maxVisibleRows =
            store.config.renderOptions.dataGridNumVisibleRows ?? 5;
        const visibleRows =
            renderedRowCount > 0
                ? Math.min(maxVisibleRows, renderedRowCount)
                : maxVisibleRows;
        const footerHeight = store.dataGridStore?.hideFooterPagination ? 0 : 52;
        const headerHeight = 52;
        const rowHeight = 43;
        const removeHeight =
            Number.parseInt(store.dataGridStore?.removeHeight ?? "0", 10) || 0;

        return `${
            headerHeight + footerHeight + rowHeight * visibleRows + removeHeight
        }px`;
    }, [
        store.dataGridStore?.hideFooterPagination,
        store.dataGridStore?.removeHeight,
        store.config.renderOptions.dataGridNumVisibleRows,
        renderedRowCount,
    ]);

    const { isDataGridVisible, toggleDataGridVisibility } =
        useDataGridVisibility(
            buildDataGridVisibilityKey(
                signalsPageStore.currentDashboardId,
                store.id,
            ),
        );

    const shouldLimitDataGridSelections =
        store.selectionType === "Modules" ||
        store.dataEndpoint ===
            SignalsEndpoints.trends.AverageScoreByQuestionOverTimeID;
    const showDataGridWarning =
        shouldLimitDataGridSelections &&
        !!store.config.dataGrid?.selection &&
        (store.dataGridStore?.selectedRowIds.length ?? 0) >=
            store.getPageSelectionLimit();

    if (store.chartType === "Table") {
        return null; // Table cards should use the TableCard component, not ChartCard
        // Returning null here to catch any charts in the backend that are stil using the old table chart type instead of the new table card object type.
        // Once we are sure there are no more of those, we can remove this check and the old table chart type entirely. (After backfills)
    }

    return (
        <Grid
            sx={{
                borderRadius: 2,
                width: "100%",
                // Fills the grid cell; the plot takes what the chrome leaves.
                height: "100%",
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                boxShadow: defaultShadowsForSignals,
                scrollMarginTop: "150px",
                backgroundColor: theme.palette.white.main,
            }}
            id={store.id}
            ref={attachCard}
        >
            <Grid
                container
                wrap="nowrap"
                justifyContent={"space-between"}
                sx={{
                    paddingInline: theme.spacing(3),
                    paddingBlock: theme.spacing(2),
                    flex: "0 0 auto",
                    width: "100%",
                }}
            >
                {/* Title takes what is left; the menu keeps its own width so a
                    long subtitle on a narrow card cannot push it onto its own
                    line. */}
                <Grid
                    container
                    item
                    direction={"column"}
                    justifyContent="center"
                    sx={{ flex: "1 1 auto", minWidth: 0 }}
                >
                    <Typography variant="h2">
                        <Grid
                            container
                            direction="row"
                            item
                            alignItems="center"
                            gap={1}
                        >
                            {store.title}
                            {deprecation && (
                                <Grid item className="no-export">
                                    <AcxDeprecatedTag
                                        infoText={deprecation.infoText}
                                    />
                                </Grid>
                            )}
                            {showDataAvailabilityInfo &&
                                dataAvailability.infoKind && (
                                    <Grid item className="no-export">
                                        <DataAvailabilityInfo
                                            kind={dataAvailability.infoKind}
                                        />
                                    </Grid>
                                )}
                            {store.config.renderOptions.patientJourneyBadge && (
                                <PatientJourneyStagesDialog />
                            )}
                            {store.config.renderOptions.isTemporal
                                ? ` - ${store.temporalGrain ?? "Daily"}`
                                : ""}
                        </Grid>
                    </Typography>

                    <Typography sx={{ color: theme.palette.neutral[600] }}>
                        {store.subtitle}
                    </Typography>
                </Grid>
                {store.config.renderOptions.menu && (
                    <Grid item alignSelf="flex-start" sx={{ flex: "0 0 auto" }}>
                        <ChartMenu
                            store={store}
                            chartContainerRef={chartContainerRef}
                            isValidData={isValidData}
                        />
                    </Grid>
                )}
            </Grid>
            <Grid sx={{ flex: "0 0 auto", width: "100%" }}>
                <Divider />
            </Grid>
            <Grid
                container
                ref={chartBodyRef}
                sx={{
                    paddingInline: theme.spacing(3),
                    paddingBlock: theme.spacing(2),
                    display: "flex",
                    flexDirection: "column",
                    flexWrap: "nowrap",
                    flex: "1 1 auto",
                    minHeight: 0,
                    width: "100%",
                    overflowY: "auto",
                }}
            >
                <Box sx={{ flex: "0 0 auto", width: "100%" }}>
                    <ObjectFiltersChips store={store} />
                    <ZoomBreadcrumbs store={store} />
                    <Grid
                        container
                        justifyContent={
                            store.selectionType === undefined // || dashboard is custom/model results group and is NOT editable
                                ? "flex-end"
                                : "space-between"
                        }
                        className="no-export" // prevent element from showing up in export to png
                    >
                        <ChartControls store={store} />
                    </Grid>
                    <ExportSelectionFields store={store} />
                </Box>
                {!store.isChartRenderingReady && (
                    <AcxEmptyState size="small" variant="loading" />
                )}
                {store.isChartRenderingReady && (
                    <>
                        <Box
                            ref={chartSizeRef}
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                flex: "1 1 auto",
                                minHeight: `${MINIMUM_CHART_PLOT_HEIGHT_PX}px`,
                                width: "100%",
                            }}
                        >
                            <Chart store={store} />
                        </Box>
                        <Box sx={{ flex: "0 0 auto", width: "100%" }}>
                            {store.isDisplayingRenderableChartData &&
                                store.shouldShowPagination && (
                                    <ChartPagination store={store} />
                                )}
                        </Box>

                        {store.isDisplayingRenderableChartData &&
                            shouldShowSpotlight && <Spotlight store={store} />}
                        {store.isDisplayingRenderableChartData &&
                            shouldShowAnalyzeWithKBo && (
                                <SignalsAnalyzeWithKboSpotlight store={store} />
                            )}
                        {store.config.dataGrid &&
                            store.dataGridStore &&
                            !!store.data &&
                            !store.isChartErrorPresent &&
                            !store.groupDeleted &&
                            !store.groupAccessDenied && (
                                <Grid
                                    container
                                    className={"no-export"} // no-export prevents this element from showing up in export to png
                                    id={"dg-" + store.id}
                                    sx={{
                                        paddingTop: isDataGridVisible
                                            ? theme.spacing(1)
                                            : theme.spacing(2),
                                    }}
                                >
                                    {isDataGridVisible && (
                                        <>
                                            {showDataGridWarning && (
                                                <Grid
                                                    item
                                                    container
                                                    xs={12}
                                                    alignItems="center"
                                                    justifyContent="flex-start"
                                                    sx={{
                                                        color: theme.palette
                                                            .error.main,
                                                        minHeight: "32px",
                                                    }}
                                                >
                                                    <Typography variant="subtitle2">
                                                        No more than{" "}
                                                        {store.getPageSelectionLimit()}{" "}
                                                        selections allowed, data
                                                        represents first{" "}
                                                        {store.getPageSelectionLimit()}{" "}
                                                        selections.
                                                    </Typography>
                                                </Grid>
                                            )}
                                            <Grid
                                                item
                                                xs={12}
                                                sx={{
                                                    height: dataGridHeight,
                                                }}
                                            >
                                                <Box
                                                    ref={dataGridFrameRef}
                                                    sx={{ height: "100%" }}
                                                >
                                                    <AcxDataGrid
                                                        dataGridStore={
                                                            store.dataGridStore
                                                        }
                                                    />
                                                </Box>
                                            </Grid>
                                        </>
                                    )}
                                    <DataGridVisibilityToggle
                                        isDataGridVisible={isDataGridVisible}
                                        onToggle={toggleDataGridVisibility}
                                    />
                                </Grid>
                            )}
                    </>
                )}
            </Grid>
        </Grid>
    );
});

export default ChartCard;
